import React, {
    ChangeEvent,
    ReactNode,
    TextareaHTMLAttributes,
    useId,
    useState,
} from "react";

import "./AppTextBox_New.scss";

export interface AppTextBox_NewProps
    extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
    label?: ReactNode;
    controlId?: string;
    error?: ReactNode;
    helperText?: ReactNode;
    showCharacterCount?: boolean;
    characterCountLabel?: ReactNode;
    resize?: "none" | "vertical" | "horizontal" | "both";
    onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    onValueChange?: (value: string) => void;
}

const AppTextBox_New: React.FC<AppTextBox_NewProps> = ({
    label,
    controlId,
    name,
    value,
    defaultValue = "",
    maxLength = 1500,
    rows = 4,
    required = false,
    disabled = false,
    readOnly = false,
    error,
    helperText,
    showCharacterCount = true,
    characterCountLabel = "Character Count",
    resize = "vertical",
    className = "",
    style,
    onChange,
    onValueChange,
    "aria-describedby": ariaDescribedBy,
    ...textareaProps
}) => {
    const generatedId = useId();
    const id = controlId ?? name ?? `app-text-box-${generatedId}`;
    const [uncontrolledValue, setUncontrolledValue] = useState(String(defaultValue));
    const currentValue = value === undefined ? uncontrolledValue : String(value ?? "");
    const messageId = `${id}-message`;
    const countId = `${id}-count`;
    const describedBy = [
        ariaDescribedBy,
        error || helperText ? messageId : undefined,
        showCharacterCount ? countId : undefined,
    ]
        .filter(Boolean)
        .join(" ") || undefined;

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        if (value === undefined) {
            setUncontrolledValue(event.target.value);
        }

        onChange?.(event);
        onValueChange?.(event.target.value);
    };

    return (
        <div
            className={`app-text-box${error ? " app-text-box--error" : ""}${disabled ? " app-text-box--disabled" : ""} ${className}`.trim()}
        >
            {label && (
                <label className="app-text-box__label" htmlFor={id}>
                    {label}
                    {required && <span className="app-text-box__required" aria-hidden="true"> *</span>}
                </label>
            )}

            <textarea
                {...textareaProps}
                id={id}
                name={name}
                value={currentValue}
                maxLength={maxLength}
                rows={rows}
                required={required}
                disabled={disabled}
                readOnly={readOnly}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy}
                className="app-text-box__control"
                style={{ ...style, resize }}
                onChange={handleChange}
            />

            <div className="app-text-box__meta">
                <span id={messageId} className={error ? "app-text-box__error" : "app-text-box__helper"}>
                    {error || helperText}
                </span>

                {showCharacterCount && (
                    <span id={countId} className="app-text-box__count" aria-live="polite">
                        {characterCountLabel}: {currentValue.length}
                        {typeof maxLength === "number" && ` / ${maxLength}`}
                    </span>
                )}
            </div>
        </div>
    );
};

export default AppTextBox_New;
